import type { Money } from '@/shared/api/types';

import { BRL, currencySymbol, moneyToInput, parseMoney } from './format';

/**
 * Mascara do campo de dinheiro. Toda a logica de `MoneyField` esta aqui, sem tela.
 *
 * O campo trabalha com dois textos. O `cru` e o que a pessoa digitou, sem nada que a
 * mascara acrescente, e e ele que o formulario recebe: `parseMoney`, `toMoney` e
 * `moneyInputError` leem o mesmo texto de antes da mascara, e a mesma digitacao da o
 * mesmo valor ou o mesmo erro (ADR 0004). O `exibido` e o cru com o simbolo e o ponto
 * de milhar, e nasce so do cru.
 *
 * Por isso a mascara nao adivinha nada. O ponto de milhar que ela pos nao existe no
 * cru: apagar um digito de "R$ 1.500" da "R$ 150", e nao 1,50. O ponto que a pessoa
 * digitou esta no cru e segue a regra do `parseMoney`: "1500.50" tem centavos
 * (SC-R21) e "1.500" continua recusado, por ser ambiguo.
 *
 * Digitar sempre conta reais: "400000" sao quatrocentos mil, e os centavos so entram
 * depois da virgula. Ninguem digita em centavos, e nao ha ponto flutuante aqui: tudo
 * e texto ate o `parseMoney`.
 */

/** O que o campo sabe mostrar com mascara. Fora disso, fica como a pessoa digitou. */
const FORMATO = /^(-?)(\d*)([.,][\d.,]*)?$/;

/** Texto que ainda nao tem nenhum digito: sinal ou separador sozinhos. */
const SEM_DIGITO = /^-?[.,]?$/;

function agrupar(inteiro: string): string {
  return inteiro.replace(/\B(?=(\d{3})+$)/g, '.');
}

/**
 * Simbolo e ponto de milhar sobre o texto cru. Texto fora do formato volta como veio,
 * para a pessoa ver o que digitou enquanto o formulario mostra o erro.
 */
export function mascararDinheiro(cru: string, currency: string = BRL): string {
  const partes = FORMATO.exec(cru);
  if (!partes) return cru;
  const [, sinal = '', inteiro = '', resto = ''] = partes;
  if (inteiro === '' && resto === '') return sinal;
  return `${sinal}${currencySymbol(currency)} ${agrupar(inteiro)}${resto}`;
}

function comCentavos(valor: Money): string {
  const texto = moneyToInput(valor) || '0';
  if (texto.includes(',') || valor.exponent === 0) return texto;
  return `${texto},${'0'.repeat(valor.exponent)}`;
}

/**
 * Texto cru de um valor pronto: ao abrir o formulario e ao sair do campo, com os
 * centavos completos. Vazio continua vazio, e o texto que o `parseMoney` recusa fica
 * como esta, para que o erro continue apontando o que foi digitado.
 */
export function cruDoValor(valor: string, currency: string = BRL): string {
  if (valor.trim() === '') return '';
  const lido = parseMoney(valor, currency);
  return lido ? comCentavos(lido) : valor;
}

/** Tira o simbolo e os espacos em volta: "R$ 1.234,56" colado vira "1.234,56". */
function semSimbolo(texto: string, currency: string): string {
  return texto
    .split(currencySymbol(currency))
    .map((parte) => parte.trim())
    .join('');
}

/** Zero a esquerda nao muda o valor, e ",5" e "0,5": a tela mostra como se le. */
function normalizar(cru: string): string {
  return cru
    .replace(/^(-?)0+(?=\d)/, '$1')
    .replace(/^(-?)(?=[.,])/, (_trecho, sinal: string) => `${sinal}0`);
}

/** Quanto do cru fica antes da posicao do texto exibido. */
function posicaoNoCru(exibido: string, cru: string, posicao: number): number {
  let lidos = 0;
  for (let i = 0; i < posicao && lidos < cru.length; i += 1) {
    if (exibido[i] === cru[lidos]) lidos += 1;
  }
  return lidos;
}

/**
 * Posicao do texto exibido logo depois de `lidos` caracteres do cru. O ponto de milhar
 * fica a direita do cursor, para o Backspace seguinte apagar o digito, e nao travar no
 * ponto. Quem apaga para a frente passa pelo ponto, pelo mesmo motivo.
 */
function posicaoNoExibido(
  exibido: string,
  cru: string,
  lidos: number,
  paraFrente: boolean,
): number {
  let i = 0;
  let contados = 0;
  while (i < exibido.length && contados < lidos) {
    if (exibido[i] === cru[contados]) contados += 1;
    i += 1;
  }
  if (paraFrente) {
    while (i < exibido.length && exibido[i] !== cru[contados]) i += 1;
  }
  // O cursor nunca fica dentro do simbolo.
  return Math.max(i, exibido.search(/\d/));
}

/** O que mudou entre o texto anterior e o novo: a faixa trocada e o que entrou. */
function trechoEditado(anterior: string, texto: string, cursor: number) {
  const depois = texto.slice(cursor);
  if (!anterior.endsWith(depois)) {
    return { inicio: 0, fim: anterior.length, inserido: texto };
  }
  const fim = anterior.length - depois.length;
  const limite = Math.min(cursor, fim);
  let inicio = 0;
  while (inicio < limite && anterior[inicio] === texto[inicio]) inicio += 1;
  return { inicio, fim, inserido: texto.slice(inicio, cursor) };
}

export interface EdicaoDeDinheiro {
  /** Texto sem mascara, base da proxima edicao. */
  cru: string;
  /** Texto que o campo mostra. */
  exibido: string;
  /** Posicao do cursor no texto exibido. */
  cursor: number;
  /** Texto do formulario: o cru, ou vazio enquanto nao ha nenhum digito. */
  valor: string;
}

function montarEdicao(
  cru: string,
  lidos: number,
  semDigito: boolean,
  paraFrente: boolean,
  currency: string,
): EdicaoDeDinheiro {
  const exibido = mascararDinheiro(cru, currency);
  return {
    cru,
    exibido,
    cursor: posicaoNoExibido(exibido, cru, lidos, paraFrente),
    valor: semDigito ? '' : cru,
  };
}

/**
 * Aplica ao texto cru a edicao que a pessoa fez no texto exibido.
 *
 * `texto` e `cursor` sao o que o navegador entregou depois da digitacao, da colagem ou
 * do apagamento. A edicao e localizada no texto exibido e levada ao cru, sem reler o
 * texto inteiro: e isso que separa o ponto da mascara do ponto digitado, e que mantem
 * o cursor no lugar quando a pessoa edita no meio do valor.
 */
export function editarDinheiro(
  cruAnterior: string,
  texto: string,
  cursor: number,
  {
    paraFrente = false,
    currency = BRL,
  }: { paraFrente?: boolean | undefined; currency?: string | undefined } = {},
): EdicaoDeDinheiro {
  const exibidoAnterior = mascararDinheiro(cruAnterior, currency);
  const { inicio, fim, inserido } = trechoEditado(exibidoAnterior, texto, cursor);
  const de = posicaoNoCru(exibidoAnterior, cruAnterior, inicio);
  const ate = posicaoNoCru(exibidoAnterior, cruAnterior, fim);

  // Apagar so a virgula de "R$ 1.500,00" juntaria os centavos ao inteiro e multiplicaria
  // o valor por cem sem aviso (SC-R21). O separador com centavos atras nao se apaga: o
  // cursor passa por ele, como passa pelo ponto de milhar.
  const separador = ate - de === 1 && /[.,]/.test(cruAnterior.charAt(de));
  if (inserido.trim() === '' && separador && ate < cruAnterior.length) {
    return montarEdicao(cruAnterior, paraFrente ? ate : de, false, false, currency);
  }

  const juntos = cruAnterior.slice(0, de) + inserido + cruAnterior.slice(ate);
  const digitado = semSimbolo(juntos, currency);
  const cru = normalizar(digitado);
  // Medido pela direita: o zero tirado a esquerda nao desloca o que vem depois.
  const aDireita = cruAnterior.length - ate;
  const lidos = Math.max(0, cru.length - aDireita);
  return montarEdicao(cru, lidos, SEM_DIGITO.test(digitado), paraFrente, currency);
}
